/* ============================================================
   repeatOrderView — CB-5 (1715d4b7). Pure view model for the
   repeat-order result card: the server re-fills the cart with
   every line that can honestly be repeated and reports the rest
   per line («файл удалён» and friends). This module turns the
   raw DTO into calm ru copy:
   - `headline`: «Добавлено в корзину: N из M» / all-skipped case;
   - `skipLines`: one line per skipped position, title + reason;
   - `tone`: success when everything repeated, accent on partial,
     danger when nothing could be repeated at all.
   Light theme, Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import type { RepeatOrderResult } from '../types/product';

export type RepeatViewTone = 'success' | 'accent' | 'danger';

export interface RepeatOrderView {
  tone: RepeatViewTone;
  headline: string;
  /** Per-line fallbacks for everything that was NOT re-added. */
  skipLines: Array<{ title: string; reason: string }>;
  /** True when at least one line landed in the cart. */
  anyAdded: boolean;
}

/** Pure view model — trivially unit-testable, no React dependency. */
export function buildRepeatOrderView(result: RepeatOrderResult): RepeatOrderView {
  const total = result.items.length;
  const added = result.items.filter((i) => i.added).length;
  const skipLines = result.items
    .filter((i) => !i.added)
    .map((i) => ({ title: i.title, reason: (i.skipReason ?? 'Недоступно для повтора').trim() }));

  if (total === 0) {
    // Defensive: the server only allows repeat on orders with lines.
    return { tone: 'danger', headline: 'В заказе нет позиций для повтора', skipLines: [], anyAdded: false };
  }

  if (added === 0) {
    return {
      tone: 'danger',
      headline: 'Не удалось повторить заказ — позиции недоступны',
      skipLines,
      anyAdded: false,
    };
  }

  return {
    tone: added === total ? 'success' : 'accent',
    headline: `Добавлено в корзину: ${added} из ${total}`,
    skipLines,
    anyAdded: true,
  };
}
